import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { FiX, FiMic, FiMicOff, FiSettings, FiVolume2, FiVolumeX } from 'react-icons/fi';
import { toast } from 'sonner';

const SERAFINA_AVATAR = '/images/serafina.png';

const VOICES = [
  { id: 'pt-BR-FranciscaNeural', name: 'Francisca', desc: 'Clássica & Natural' },
  { id: 'pt-BR-ThalitaMultilingualNeural', name: 'Thalita', desc: 'Suave & Clara' },
  { id: 'en-US-AvaMultilingualNeural', name: 'Ava', desc: 'Calma & Elegante' },
  { id: 'en-US-EmmaMultilingualNeural', name: 'Emma', desc: 'Viva & Expressiva' }
];

// Clean raw markdown elements from text for spoken subtitles
const cleanSubtitleText = (text) => {
  if (!text) return '';
  return text
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    .replace(/^#+\s+/gm, '')
    .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1')
    .replace(/^\s*>\s+/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/<\/?[^>]+(>|$)/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

// Segment speech text into natural subtitle chunks (1-2 lines each)
const segmentIntoSubtitles = (cleanText) => {
  if (!cleanText) return [];
  const sentences = cleanText
    .split(/(?<=[.?!])\s+/)
    .map(s => s.trim())
    .filter(Boolean);

  const chunks = [];
  for (const sentence of sentences) {
    if (sentence.length > 100) {
      const parts = sentence.split(/(?<=[,;])\s+/).filter(Boolean);
      let currentChunk = '';
      for (const part of parts) {
        if (currentChunk && (currentChunk.length + part.length) > 90) {
          chunks.push(currentChunk.trim());
          currentChunk = part;
        } else {
          currentChunk = currentChunk ? `${currentChunk} ${part}` : part;
        }
      }
      if (currentChunk) chunks.push(currentChunk.trim());
    } else {
      chunks.push(sentence);
    }
  }

  return chunks.length > 0 ? chunks : [cleanText];
};

export const SerafinaVoiceMode = ({ messages, setMessages, onClose, API }) => {
  const [status, setStatus] = useState('listening'); // 'listening' | 'thinking' | 'speaking' | 'muted'
  const [subtitle, setSubtitle] = useState('Estou ouvindo... Pode falar qualquer dúvida ou instrução.');
  const [isMuted, setIsMuted] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);

  // Settings states
  const [showSettings, setShowSettings] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(() => {
    return localStorage.getItem('serafina_selected_voice') || 'pt-BR-FranciscaNeural';
  });
  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem('serafina_voice_volume');
    return saved !== null ? parseInt(saved, 10) : 90;
  });
  const [speed, setSpeed] = useState(() => {
    return localStorage.getItem('serafina_voice_speed') || '1.0';
  });

  // Refs for audio handling
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const currentAudioRef = useRef(null);
  const animFrameRef = useRef(null);
  const settingsRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isClosedRef = useRef(false);

  // VAD tracking refs
  const speechStartTimeRef = useRef(0);
  const silenceStartTimeRef = useRef(0);
  const isRecordingRef = useRef(false);
  const isUserSpeakingRef = useRef(false);
  const statusRef = useRef('listening');
  const isMutedRef = useRef(false);
  const isProcessingRef = useRef(false);
  const messagesRef = useRef(messages);
  const setMessagesRef = useRef(setMessages);
  const onCloseRef = useRef(onClose);
  const selectedVoiceRef = useRef(selectedVoice);
  const speedRef = useRef(speed);
  const volumeRef = useRef(volume);

  // Ref bridges for functions used inside audio loops to avoid effect re-runs
  const startRecordingTurnRef = useRef(null);
  const stopRecordingTurnRef = useRef(null);
  const stopSerafinaSpeechRef = useRef(null);
  const updateStatusRef = useRef(null);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    setMessagesRef.current = setMessages;
  }, [setMessages]);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  useEffect(() => {
    selectedVoiceRef.current = selectedVoice;
  }, [selectedVoice]);

  useEffect(() => {
    speedRef.current = speed;
  }, [speed]);

  useEffect(() => {
    volumeRef.current = volume;
  }, [volume]);

  const getRateParam = (spd) => {
    switch (spd) {
      case '1.2': return '+20%';
      case '1.5': return '+50%';
      case '2.0': return '+100%';
      default: return '+0%';
    }
  };

  // Helper to resume AudioContext if suspended by browser autoplay policy
  const resumeAudioContext = useCallback(() => {
    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume().catch(() => {});
    }
  }, []);

  // Synchronous status updater for both state and ref
  const updateStatus = useCallback((newStatus) => {
    statusRef.current = newStatus;
    setStatus(newStatus);
  }, []);

  // Clean stop for any currently playing audio
  const stopSerafinaSpeech = useCallback(() => {
    if (currentAudioRef.current) {
      const audio = currentAudioRef.current;
      currentAudioRef.current = null;
      audio.ontimeupdate = null;
      audio.onplay = null;
      audio.onended = null;
      audio.onerror = null;
      try {
        audio.pause();
        audio.currentTime = 0;
        audio.src = '';
        audio.load();
      } catch (e) {}
    }
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      try {
        window.speechSynthesis.cancel();
      } catch (e) {}
    }
    setIsAudioPlaying(false);
  }, []);

  // Play Serafina's speech with edge-tts and movie-like synchronized subtitles
  const playSerafinaVoice = useCallback(async (textToSpeak) => {
    if (isClosedRef.current) return;
    if (!textToSpeak) {
      updateStatus(isMutedRef.current ? 'muted' : 'listening');
      isProcessingRef.current = false;
      setIsAudioPlaying(false);
      return;
    }

    stopSerafinaSpeech();

    // Clean markdown and segment into movie subtitles
    const cleanText = cleanSubtitleText(textToSpeak);
    const subtitleChunks = segmentIntoSubtitles(cleanText);
    const totalChars = subtitleChunks.reduce((acc, c) => acc + c.length, 0) || 1;
    let accumulated = 0;
    const timedChunks = subtitleChunks.map(chunk => {
      const start = accumulated / totalChars;
      accumulated += chunk.length;
      const end = accumulated / totalChars;
      return { text: chunk, start, end };
    });

    setSubtitle(timedChunks[0]?.text || cleanText);

    try {
      const rateParam = getRateParam(speedRef.current);
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const response = await axios.post(
        `${API}/ai/tts`,
        {
          text: textToSpeak,
          voice: selectedVoiceRef.current,
          rate: rateParam
        },
        { responseType: 'blob', withCredentials: true, signal: controller.signal }
      );

      if (isClosedRef.current) return;

      const audioBlob = response.data;
      if (!audioBlob || audioBlob.size < 200) {
        throw new Error('Retorno de áudio vazio');
      }

      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      const curVol = typeof volumeRef.current === 'number' ? volumeRef.current : 90;
      audio.volume = Math.max(0, Math.min(1, curVol / 100));

      if (isClosedRef.current) {
        try {
          audio.pause();
          audio.src = '';
        } catch (e) {}
        return;
      }

      currentAudioRef.current = audio;

      // Start pulsing and update status to speaking when audio begins
      audio.onplay = () => {
        if (isClosedRef.current) {
          try {
            audio.pause();
            audio.src = '';
          } catch (e) {}
          return;
        }
        setIsAudioPlaying(true);
        updateStatus('speaking');
        setSubtitle(timedChunks[0]?.text || cleanText);
      };

      // Progressive movie subtitle syncing based on playback progress
      audio.ontimeupdate = () => {
        if (!audio.duration || audio.paused || audio.ended) return;
        const progress = audio.currentTime / audio.duration;
        const current = timedChunks.find(c => progress >= c.start && progress < c.end)
          || (progress >= 1 ? timedChunks[timedChunks.length - 1] : timedChunks[0]);
        if (current && current.text) {
          setSubtitle(prev => (prev !== current.text ? current.text : prev));
        }
      };

      audio.onended = () => {
        currentAudioRef.current = null;
        setIsAudioPlaying(false);
        isProcessingRef.current = false;
        if (!isMutedRef.current) {
          updateStatus('listening');
          setSubtitle('Estou ouvindo... Pode falar.');
        } else {
          updateStatus('muted');
          setSubtitle('Microfone desativado. Clique no microfone para falar.');
        }
      };

      audio.onerror = (e) => {
        console.error('TTS playback error:', e);
        currentAudioRef.current = null;
        setIsAudioPlaying(false);
        isProcessingRef.current = false;
        if (!isMutedRef.current) {
          updateStatus('listening');
          setSubtitle('Estou ouvindo...');
        } else {
          updateStatus('muted');
          setSubtitle('Microfone desativado.');
        }
      };

      if (!isClosedRef.current) {
        await audio.play();
      } else {
        try {
          audio.pause();
          audio.src = '';
        } catch (e) {}
      }
    } catch (err) {
      if (axios.isCancel(err) || err.name === 'CanceledError' || err.name === 'AbortError') {
        return;
      }
      console.error('Error in TTS generation or playback:', err);
      currentAudioRef.current = null;
      setIsAudioPlaying(false);
      isProcessingRef.current = false;
      if (!isMutedRef.current) {
        updateStatus('listening');
        setSubtitle('Não foi possível gerar a voz no momento.');
      } else {
        updateStatus('muted');
        setSubtitle('Microfone desativado.');
      }
    }
  }, [API, stopSerafinaSpeech, updateStatus]);

  // Play preview when selecting a voice or speed in settings
  const previewVoice = useCallback(async (voiceId, customSpeed) => {
    if (isClosedRef.current) return;
    try {
      stopSerafinaSpeech();
      const chosenSpeed = customSpeed || speedRef.current;
      const rateParam = getRateParam(chosenSpeed);
      const resp = await axios.post(
        `${API}/ai/tts`,
        { text: 'Olá! Sou a Serafina, sua assistente financeira.', voice: voiceId || selectedVoiceRef.current, rate: rateParam },
        { responseType: 'blob', withCredentials: true }
      );
      if (isClosedRef.current) return;
      const audioUrl = URL.createObjectURL(resp.data);
      const audio = new Audio(audioUrl);
      const curVol = typeof volumeRef.current === 'number' ? volumeRef.current : 90;
      audio.volume = Math.max(0, Math.min(1, curVol / 100));
      if (isClosedRef.current) {
        try {
          audio.pause();
          audio.src = '';
        } catch (e) {}
        return;
      }
      currentAudioRef.current = audio;

      audio.onplay = () => setIsAudioPlaying(true);
      audio.onended = () => {
        currentAudioRef.current = null;
        setIsAudioPlaying(false);
      };
      audio.onerror = () => {
        currentAudioRef.current = null;
        setIsAudioPlaying(false);
      };

      await audio.play();
    } catch (e) {
      console.error('Failed to preview voice:', e);
    }
  }, [API, stopSerafinaSpeech]);

  // Process user speech turn: transcribe and call AI
  const processUserAudio = useCallback(async (audioBlob) => {
    if (isClosedRef.current || isProcessingRef.current) {
      return;
    }
    if (!audioBlob || audioBlob.size < 100) {
      isProcessingRef.current = false;
      setIsUserSpeaking(false);
      isUserSpeakingRef.current = false;
      if (!isMutedRef.current) {
        updateStatus('listening');
      }
      return;
    }

    isProcessingRef.current = true;
    setIsUserSpeaking(false);
    isUserSpeakingRef.current = false;
    updateStatus('thinking');
    setSubtitle('Processando o que você falou...');

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      // 1. Transcribe audio
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(audioBlob);
      });
      const base64Audio = await base64Promise;

      if (isClosedRef.current) return;

      const transcribeResp = await axios.post(
        `${API}/ai/transcribe`,
        {
          audio_base64: base64Audio,
          mime_type: audioBlob.type || 'audio/webm'
        },
        { withCredentials: true, signal: controller.signal }
      );

      if (isClosedRef.current) return;

      const userText = transcribeResp.data?.text?.trim();

      if (!userText) {
        // Ignored noise / silence / unrecognized speech
        isProcessingRef.current = false;
        setIsUserSpeaking(false);
        isUserSpeakingRef.current = false;
        updateStatus(isMutedRef.current ? 'muted' : 'listening');
        setSubtitle(isMutedRef.current ? 'Microfone desativado.' : 'Não consegui entender com clareza. Pode falar novamente?');
        return;
      }

      // Add user turn to chat history
      const newUserMsg = { role: 'user', content: userText };
      setMessagesRef.current?.(prev => [...prev, newUserMsg]);
      setSubtitle(`"${userText}"`);

      // 2. Call Serafina AI Chat
      const currentHistory = (messagesRef.current || [])
        .filter(m => !m.error)
        .map(m => ({ role: m.role, content: m.content }));

      if (isClosedRef.current) return;

      const chatResp = await axios.post(
        `${API}/ai/chat`,
        {
          message: userText,
          chat_history: currentHistory
        },
        { withCredentials: true, signal: controller.signal }
      );

      if (isClosedRef.current) return;

      const aiResponse = chatResp.data?.response || 'Não consegui processar, pode repetir?';

      // Handle tour redirection if needed
      if (aiResponse.includes('[REFAZER_TOUR]')) {
        localStorage.removeItem('finance_tour_done');
        window.location.href = '/dashboard';
        return;
      }

      // Add assistant response to history
      setMessagesRef.current?.(prev => [...prev, { role: 'assistant', content: aiResponse }]);

      // Notify if items created
      if (chatResp.data?.created_items?.length > 0) {
        chatResp.data.created_items.forEach(item => {
          if (item.type === 'registro') {
            toast.success(`Registro "${item.description}" de R$ ${item.value.toFixed(2)} criado!`);
          } else if (item.type === 'meta') {
            toast.success(`Meta "${item.title}" de R$ ${item.target_amount.toFixed(2)} criada!`);
          }
        });
      }

      // 3. Serafina speaks the answer out loud (stays in processing state until playSerafinaVoice sets speaking)
      if (isClosedRef.current) return;
      await playSerafinaVoice(aiResponse);

    } catch (err) {
      if (axios.isCancel(err) || err.name === 'CanceledError' || err.name === 'AbortError') {
        return;
      }
      console.error('Error processing conversational audio turn:', err);
      isProcessingRef.current = false;
      setIsUserSpeaking(false);
      isUserSpeakingRef.current = false;
      updateStatus(isMutedRef.current ? 'muted' : 'listening');
      setSubtitle(isMutedRef.current ? 'Microfone desativado.' : 'Ocorreu uma instabilidade. Pode falar novamente?');
    }
  }, [API, playSerafinaVoice, updateStatus]);

  // Start internal MediaRecorder chunk collector
  const startRecordingTurn = useCallback(() => {
    if (!streamRef.current || isRecordingRef.current || isProcessingRef.current || isClosedRef.current) return;
    try {
      audioChunksRef.current = [];
      let mimeType = 'audio/webm';
      if (typeof MediaRecorder !== 'undefined' && typeof MediaRecorder.isTypeSupported === 'function') {
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          mimeType = 'audio/webm;codecs=opus';
        } else if (MediaRecorder.isTypeSupported('audio/webm')) {
          mimeType = 'audio/webm';
        } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
        }
      }
      const mediaRecorder = new MediaRecorder(streamRef.current, { mimeType });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        isRecordingRef.current = false;
        setIsUserSpeaking(false);
        isUserSpeakingRef.current = false;
        const blob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType || mimeType || 'audio/webm' });
        processUserAudio(blob);
      };

      mediaRecorder.start(100);
      isRecordingRef.current = true;
      setIsUserSpeaking(true);
      isUserSpeakingRef.current = true;
    } catch (err) {
      console.error('Failed to start MediaRecorder:', err);
    }
  }, [processUserAudio]);

  // Stop internal MediaRecorder
  const stopRecordingTurn = useCallback(() => {
    if (mediaRecorderRef.current && isRecordingRef.current) {
      try {
        if (mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      } catch (e) {
        console.error('Error stopping recorder:', e);
      }
    }
    isRecordingRef.current = false;
    setIsUserSpeaking(false);
    isUserSpeakingRef.current = false;
  }, []);

  // Update bridges so audio loop can call latest functions without triggering useEffect re-runs
  useEffect(() => {
    startRecordingTurnRef.current = startRecordingTurn;
  }, [startRecordingTurn]);

  useEffect(() => {
    stopRecordingTurnRef.current = stopRecordingTurn;
  }, [stopRecordingTurn]);

  useEffect(() => {
    stopSerafinaSpeechRef.current = stopSerafinaSpeech;
  }, [stopSerafinaSpeech]);

  useEffect(() => {
    updateStatusRef.current = updateStatus;
  }, [updateStatus]);

  // Dedicated safe close handler that halts audio, cancels in-flight requests and releases resources
  const handleClose = useCallback(() => {
    if (isClosedRef.current) return;
    isClosedRef.current = true;
    if (abortControllerRef.current) {
      try {
        abortControllerRef.current.abort();
      } catch (e) {}
    }
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
    }
    stopSerafinaSpeech();
    stopRecordingTurn();
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach(t => t.stop());
      } catch (e) {}
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      try {
        audioContextRef.current.close().catch(() => {});
      } catch (e) {}
    }
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      try {
        window.speechSynthesis.cancel();
      } catch (e) {}
    }
    onCloseRef.current?.();
  }, [stopRecordingTurn, stopSerafinaSpeech]);

  // Initialize Microphone and Web Audio VAD
  useEffect(() => {
    let mounted = true;
    isClosedRef.current = false;

    const initAudio = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });

        if (!mounted || isClosedRef.current) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }

        streamRef.current = stream;
        stream.getAudioTracks().forEach(t => { t.enabled = true; });

        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        const audioCtx = new AudioContextClass();
        audioContextRef.current = audioCtx;

        if (audioCtx.state === 'suspended') {
          audioCtx.resume().catch(() => {});
        }

        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.2;
        source.connect(analyser);
        analyserRef.current = analyser;

        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        const timeArray = new Uint8Array(analyser.fftSize);

        // VAD parameters
        // Human speech energy is concentrated in lower/mid frequency bins (~90Hz to ~3500Hz)
        const SPEECH_THRESHOLD = 11; // Speech bins energy threshold
        const TIME_THRESHOLD = 4.5; // Waveform variation threshold
        const BARGE_IN_SPEECH_THRESHOLD = 36; // Energy threshold for interrupting Serafina
        const BARGE_IN_TIME_THRESHOLD = 14;
        const SILENCE_TIMEOUT = 1100; // 1.1s natural pause to finalize speech

        const checkAudioLoop = () => {
          if (!mounted || isClosedRef.current) return;

          if (audioCtx.state === 'suspended') {
            audioCtx.resume().catch(() => {});
          }

          analyser.getByteFrequencyData(dataArray);
          analyser.getByteTimeDomainData(timeArray);

          // Focus on human speech frequencies (~90Hz to ~3500Hz, bins 1 to 38)
          const speechBins = Math.min(38, dataArray.length);
          let speechSum = 0;
          for (let i = 1; i <= speechBins; i++) {
            speechSum += dataArray[i];
          }
          const speechVolume = speechBins > 0 ? speechSum / speechBins : 0;

          // Waveform deviation from silence (128)
          let timeSum = 0;
          for (let i = 0; i < timeArray.length; i++) {
            timeSum += Math.abs(timeArray[i] - 128);
          }
          const timeVolume = timeArray.length > 0 ? timeSum / timeArray.length : 0;

          const isVoiceActive = speechVolume > SPEECH_THRESHOLD || timeVolume > TIME_THRESHOLD;
          const isBargeInActive = speechVolume > BARGE_IN_SPEECH_THRESHOLD || timeVolume > BARGE_IN_TIME_THRESHOLD;

          const now = Date.now();
          const currentStatus = statusRef.current;
          const muted = isMutedRef.current;

          if (!muted) {
            // Case 1: Serafina is speaking and user interrupts (Barge-in)
            // ONLY allow interrupt if audio is actively playing through speakers
            if (currentStatus === 'speaking' && currentAudioRef.current && !currentAudioRef.current.paused) {
              if (isBargeInActive) {
                if (!speechStartTimeRef.current) {
                  speechStartTimeRef.current = now;
                } else if (now - speechStartTimeRef.current > 350) { // Sustained speech for 350ms
                  // USER INTERRUPTED! Stop Serafina and start listening
                  stopSerafinaSpeechRef.current?.();
                  isProcessingRef.current = false;
                  updateStatusRef.current?.('listening');
                  setSubtitle('Ouvindo você...');
                  silenceStartTimeRef.current = 0;
                  speechStartTimeRef.current = 0;
                  startRecordingTurnRef.current?.();
                }
              } else {
                speechStartTimeRef.current = 0;
              }
            }

            // Case 2: Listening for user speech turn
            else if (currentStatus === 'listening' && !isProcessingRef.current) {
              if (isVoiceActive) {
                // User is talking
                if (!isRecordingRef.current) {
                  startRecordingTurnRef.current?.();
                }
                silenceStartTimeRef.current = 0;
                if (!isUserSpeakingRef.current) {
                  isUserSpeakingRef.current = true;
                  setIsUserSpeaking(true);
                }
              } else if (isRecordingRef.current) {
                // User became silent
                if (!silenceStartTimeRef.current) {
                  silenceStartTimeRef.current = now;
                } else if (now - silenceStartTimeRef.current > SILENCE_TIMEOUT) {
                  // Finalize turn!
                  silenceStartTimeRef.current = 0;
                  if (isUserSpeakingRef.current) {
                    isUserSpeakingRef.current = false;
                    setIsUserSpeaking(false);
                  }
                  stopRecordingTurnRef.current?.();
                }
              }
            }
          }

          animFrameRef.current = requestAnimationFrame(checkAudioLoop);
        };

        animFrameRef.current = requestAnimationFrame(checkAudioLoop);

      } catch (err) {
        console.error('Error starting audio in voice mode:', err);
        toast.error('Não foi possível acessar o microfone.');
        onCloseRef.current?.();
      }
    };

    initAudio();

    return () => {
      mounted = false;
      isClosedRef.current = true;
      if (abortControllerRef.current) {
        try { abortControllerRef.current.abort(); } catch (e) {}
      }
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      stopSerafinaSpeechRef.current?.();
      stopRecordingTurnRef.current?.();
      if (streamRef.current) {
        try { streamRef.current.getTracks().forEach(t => t.stop()); } catch (e) {}
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        try { audioContextRef.current.close().catch(() => {}); } catch (e) {}
      }
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        try { window.speechSynthesis.cancel(); } catch (e) {}
      }
    };
  }, []);

  // Handle keyboard Escape to exit
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showSettings) {
          setShowSettings(false);
        } else {
          handleClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleClose, showSettings]);

  // Close settings popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setShowSettings(false);
      }
    };
    if (showSettings) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSettings]);

  const toggleMute = () => {
    resumeAudioContext();
    if (isMuted) {
      setIsMuted(false);
      isMutedRef.current = false;
      toast.info('Microfone ativado');
      if (statusRef.current !== 'speaking' && statusRef.current !== 'thinking' && !isProcessingRef.current) {
        updateStatus('listening');
        setSubtitle('Estou ouvindo... Pode falar.');
      }
    } else {
      setIsMuted(true);
      isMutedRef.current = true;
      setIsUserSpeaking(false);
      isUserSpeakingRef.current = false;
      toast.info('Microfone desativado');

      // If user was speaking into mic, stop and immediately process turn without waiting
      if (isRecordingRef.current) {
        stopRecordingTurn();
        updateStatus('thinking');
        setSubtitle('Processando o que você falou...');
      } else if (statusRef.current !== 'speaking' && statusRef.current !== 'thinking' && !isProcessingRef.current) {
        updateStatus('muted');
        setSubtitle('Microfone desativado. Clique nele para falar.');
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#070D1A]/95 backdrop-blur-2xl flex flex-col items-center justify-between p-6 sm:p-10 select-none animate-in fade-in duration-300"
      data-testid="serafina-voice-mode-modal"
      onClick={resumeAudioContext}
      onTouchStart={resumeAudioContext}
    >
      {/* Top Bar with Mode Name */}
      <div className="w-full max-w-xl flex items-center justify-between text-xs text-slate-400">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
          <span className="font-semibold uppercase tracking-wider text-slate-300">Modo Conversa por Voz</span>
        </div>
        <div className="text-[11px] text-slate-500 hidden sm:inline">
          Pressione <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-slate-300 font-mono">Esc</kbd> ou clique no X para sair
        </div>
      </div>

      {/* Center: Serafina Avatar with Soft Aura (Smooth, soothing pulsation when speaking) */}
      <div className="flex flex-col items-center justify-center my-auto relative">
        <div className="relative flex items-center justify-center">
          {/* Outermost halo: Soft, soothing ambient glow behind Serafina */}
          <div
            className={`absolute rounded-full pointer-events-none transition-all duration-700 ease-in-out ${
              isAudioPlaying
                ? 'w-64 h-64 sm:w-72 sm:h-72 bg-gradient-to-r from-blue-600/30 via-cyan-400/25 to-indigo-600/30 blur-3xl serafina-glow-playing'
                : isUserSpeaking
                ? 'w-64 h-64 sm:w-72 sm:h-72 bg-gradient-to-r from-emerald-500/30 via-cyan-400/30 to-blue-500/30 blur-3xl animate-pulse'
                : status === 'thinking' || status === 'speaking'
                ? 'w-60 h-60 bg-amber-500/20 blur-2xl animate-pulse opacity-60'
                : status === 'muted'
                ? 'w-56 h-56 bg-slate-700/10 blur-xl opacity-20'
                : 'w-56 h-56 bg-blue-600/15 blur-2xl opacity-30'
            }`}
          />

          {/* Subtle concentric ring: soft, elegant border with gentle breathing - NO aggressive ping circles */}
          <div
            className={`absolute rounded-full pointer-events-none transition-all duration-700 ease-in-out ${
              isAudioPlaying
                ? 'w-48 h-48 sm:w-56 sm:h-56 border border-cyan-400/35 serafina-ring-playing'
                : isUserSpeaking
                ? 'w-48 h-48 sm:w-56 sm:h-56 border-2 border-emerald-400/60 animate-pulse'
                : status === 'listening'
                ? 'w-44 h-44 border border-blue-500/20'
                : status === 'thinking' || status === 'speaking'
                ? 'w-44 h-44 border border-amber-400/30 animate-spin'
                : 'w-40 h-40 border border-slate-700/20'
            }`}
          />

          {/* Central Avatar: smooth subtle ring and gentle elevation without jarring pulse */}
          <button
            type="button"
            onClick={() => {
              resumeAudioContext();
              if (isAudioPlaying || status === 'speaking') {
                stopSerafinaSpeech();
                isProcessingRef.current = false;
                updateStatus(isMutedRef.current ? 'muted' : 'listening');
                setSubtitle(isMutedRef.current ? 'Microfone desativado.' : 'Estou ouvindo... Pode falar.');
              }
            }}
            className={`relative w-36 h-36 sm:w-44 sm:h-44 rounded-full overflow-hidden p-1 shadow-2xl bg-gradient-to-tr from-blue-900 via-indigo-800 to-cyan-500 focus:outline-none transition-all duration-500 ${
              isAudioPlaying
                ? 'scale-[1.02] ring-2 ring-cyan-400/40 cursor-pointer shadow-cyan-500/20'
                : isUserSpeaking
                ? 'scale-[1.03] ring-2 ring-emerald-400/60 shadow-emerald-500/30'
                : 'scale-100 cursor-default ring-1 ring-white/10'
            }`}
            title={isAudioPlaying ? 'Clique para interromper a Serafina' : 'Serafina'}
          >
            <img
              src={SERAFINA_AVATAR}
              alt="Serafina"
              className="w-full h-full object-cover rounded-full pointer-events-none"
            />
          </button>
        </div>

        {/* Status indicator badge */}
        <div className="mt-8 flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-md shadow-sm">
          <span
            className={`w-2 h-2 rounded-full ${
              isAudioPlaying
                ? 'bg-cyan-400 animate-pulse'
                : isUserSpeaking
                ? 'bg-emerald-400 animate-ping'
                : status === 'thinking' || status === 'speaking'
                ? 'bg-amber-400 animate-spin'
                : status === 'muted'
                ? 'bg-slate-500'
                : 'bg-emerald-400 animate-pulse'
            }`}
          />
          <span className="text-xs font-medium text-slate-200">
            {isAudioPlaying
              ? (isMuted ? 'Serafina falando... (microfone desligado)' : 'Serafina falando... (clique nela para pausar)')
              : isUserSpeaking
              ? 'Gravando sua fala... (fale normalmente)'
              : status === 'thinking' || status === 'speaking'
              ? (isMuted ? 'Pensando na resposta... (microfone desligado)' : 'Pensando na resposta...')
              : status === 'muted'
              ? 'Microfone desativado (clique para falar)'
              : 'Ouvindo você... (pode falar)'}
          </span>
        </div>

        {/* Movie Subtitle / Speech Caption Box */}
        <div className="mt-5 max-w-lg sm:max-w-2xl w-full text-center px-4 min-h-[72px] sm:min-h-[84px] flex items-center justify-center">
          <div
            key={subtitle}
            className={`transition-all duration-300 ease-out transform animate-in fade-in-50 zoom-in-95 ${
              isAudioPlaying
                ? 'bg-slate-950/40 border border-white/10 backdrop-blur-md px-6 py-3 rounded-2xl shadow-xl'
                : 'bg-transparent px-4 py-2'
            }`}
          >
            <p
              className={`leading-relaxed text-center ${
                isAudioPlaying
                  ? 'text-base sm:text-lg text-white font-medium drop-shadow-md tracking-wide'
                  : 'text-sm sm:text-base text-slate-300 font-light'
              }`}
            >
              {subtitle}
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Floating Pill Bar with Settings Popover (Inspired by Copilot and ChatGPT Voice) */}
      <div className="w-full flex flex-col items-center justify-center pb-2 relative" ref={settingsRef}>
        {/* Settings Popover */}
        {showSettings && (
          <div
            className="absolute bottom-20 z-40 bg-[#0E1B33]/95 border border-white/15 backdrop-blur-2xl rounded-2xl p-4 w-[310px] sm:w-[340px] shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-150"
            data-testid="voice-mode-settings-popover"
          >
            <div className="flex items-center justify-between pb-2 mb-3 border-b border-white/10">
              <span className="text-xs font-bold text-white uppercase tracking-wider">Configurações de Voz</span>
              <button
                type="button"
                onClick={() => setShowSettings(false)}
                className="text-slate-400 hover:text-white p-1 rounded-md cursor-pointer transition-colors"
                title="Fechar configurações"
              >
                <FiX size={14} />
              </button>
            </div>

            {/* Voices Selection: 2x2 Grid (4 options) matching Copilot style */}
            <div className="mb-4">
              <span className="text-[11px] font-semibold text-slate-300 block mb-2">Voz da Serafina</span>
              <div className="grid grid-cols-2 gap-2">
                {VOICES.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => {
                      setSelectedVoice(v.id);
                      localStorage.setItem('serafina_selected_voice', v.id);
                      previewVoice(v.id, speed);
                    }}
                    className={`flex flex-col items-center justify-center py-2.5 px-2 rounded-xl border text-center transition-all cursor-pointer ${
                      selectedVoice === v.id
                        ? 'bg-blue-600/35 border-cyan-400 text-white shadow-md ring-1 ring-cyan-400/60'
                        : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <span className="text-xs font-bold leading-tight">{v.name}</span>
                    <span className="text-[10px] text-slate-400 mt-0.5">{v.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Volume Slider */}
            <div className="mb-3.5">
              <div className="flex items-center justify-between text-[11px] font-semibold text-slate-300 mb-1.5">
                <span className="flex items-center gap-1.5">
                  {volume === 0 ? <FiVolumeX size={13} className="text-red-400" /> : <FiVolume2 size={13} className="text-cyan-400" />}
                  Volume
                </span>
                <span className="font-mono text-slate-400">{volume}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={volume}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  setVolume(val);
                  localStorage.setItem('serafina_voice_volume', String(val));
                  if (currentAudioRef.current) {
                    currentAudioRef.current.volume = val / 100;
                  }
                }}
                className="w-full h-1.5 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-cyan-400"
              />
            </div>

            {/* Speed Selector: 4 options in 2x2 grid (1.0x, 1.2x, 1.5x, 2.0x) */}
            <div>
              <span className="text-[11px] font-semibold text-slate-300 block mb-1.5">Velocidade da Fala</span>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: '1.0', label: '1.0x (Normal)' },
                  { id: '1.2', label: '1.2x (Ágil)' },
                  { id: '1.5', label: '1.5x (Rápido)' },
                  { id: '2.0', label: '2.0x (Máximo)' }
                ].map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => {
                      setSpeed(s.id);
                      localStorage.setItem('serafina_voice_speed', s.id);
                      previewVoice(selectedVoice, s.id);
                    }}
                    className={`py-2 px-2 rounded-lg text-xs font-medium transition-all cursor-pointer text-center ${
                      speed === s.id
                        ? 'bg-cyan-500/30 border border-cyan-400 text-white font-bold ring-1 ring-cyan-400/50 shadow-sm'
                        : 'bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Floating Pill Bar */}
        <div className="bg-[#0B1528]/90 border border-white/15 backdrop-blur-2xl rounded-full px-5 py-2 flex items-center gap-6 shadow-2xl">
          {/* Close 'X' Button: Returns to text mode */}
          <button
            type="button"
            data-testid="voice-mode-close-button"
            onClick={handleClose}
            className="w-10 h-10 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-all cursor-pointer"
            title="Voltar para a tela de texto (Esc)"
          >
            <FiX size={20} />
          </button>

          {/* Central Microphone Button: Toggles Mute */}
          <button
            type="button"
            data-testid="voice-mode-mic-toggle"
            onClick={toggleMute}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all cursor-pointer shadow-lg ${
              isMuted
                ? 'bg-slate-800 text-slate-400 border border-slate-700 hover:bg-slate-700 hover:text-white'
                : isUserSpeaking
                ? 'bg-emerald-600 text-white ring-4 ring-emerald-500/40 shadow-emerald-500/30 animate-pulse'
                : status === 'speaking'
                ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-400/40 hover:bg-cyan-500/30'
                : 'bg-blue-600 text-white hover:bg-blue-500 ring-4 ring-blue-500/30 animate-pulse'
            }`}
            title={isMuted ? 'Retomar microfone' : 'Pausar microfone temporariamente'}
          >
            {isMuted ? <FiMicOff size={20} /> : <FiMic size={20} />}
          </button>

          {/* Settings Gear Button: Opens Popover */}
          <button
            type="button"
            data-testid="voice-mode-settings-button"
            onClick={() => setShowSettings(prev => !prev)}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all cursor-pointer ${
              showSettings
                ? 'bg-white/20 text-white rotate-45'
                : 'text-slate-400 hover:text-white hover:bg-white/10'
            }`}
            title="Ajustar vozes, volume e velocidade"
          >
            <FiSettings size={19} className="transition-transform duration-200" />
          </button>
        </div>
      </div>
    </div>
  );
};
